import React, { useEffect } from 'react';
import { StyleSheet, View } from 'react-native';
import Svg, { Circle, Defs, LinearGradient, RadialGradient, Stop } from 'react-native-svg';
import Animated, {
  cancelAnimation,
  Easing,
  useAnimatedProps,
  useAnimatedStyle,
  useReducedMotion,
  useSharedValue,
  withRepeat,
  withSequence,
  withTiming,
} from 'react-native-reanimated';
import { BrandLogo } from '../BrandLogo';
import { Check } from '../icons';
import { Apparition } from './Apparition';
import { colors, gradients } from '../../theme/tokens';

const TAILLE = 88;
const TRAIT = 6;
const RAYON = (TAILLE - TRAIT) / 2;
const CIRCONFERENCE = 2 * Math.PI * RAYON;
const ZONE = TAILLE * 1.5;

const CercleAnime = Animated.createAnimatedComponent(Circle);

/** Part de l'anneau à remplir : un palier par étape rendue, plein quand le dossier est prêt. */
export function partAnneau(faites: number, total: number, fini: boolean): number {
  if (fini) return 1;
  return total > 0 ? Math.max(0, Math.min(1, faites / total)) : 0;
}

export interface AnneauProgressionProps {
  /** Étapes RENDUES par le serveur : l'anneau avance par paliers, jamais entre deux retours. */
  faites: number;
  total: number;
  /** Préparation en cours : le halo pulse. */
  actif: boolean;
  /** Dossier prêt : anneau complet en vert, coche au centre. */
  fini: boolean;
}

/**
 * Élément du haut de l'écran de préparation : un anneau dans l'esprit de ScoreRing, tracé en dégradé
 * cyan → bleu électrique, qui se remplit d'un palier à chaque étape rendue. La marque Horméia au
 * centre (la marque seule : ses couleurs vives se lisent sur bleu nuit, pas le mot « Horméia »).
 * Décoratif pour le lecteur d'écran : la barre de progression et les étapes portent l'information.
 */
export function AnneauProgression({ faites, total, actif, fini }: AnneauProgressionProps) {
  const reduit = useReducedMotion();
  const part = useSharedValue(partAnneau(faites, total, fini));
  const souffle = useSharedValue(0);

  useEffect(() => {
    const cible = partAnneau(faites, total, fini);
    part.value = reduit ? cible : withTiming(cible, { duration: 600, easing: Easing.inOut(Easing.cubic) });
  }, [faites, total, fini, reduit, part]);

  useEffect(() => {
    if (actif && !fini && !reduit) {
      souffle.value = withRepeat(withSequence(withTiming(1, { duration: 2000 }), withTiming(0, { duration: 2000 })), -1, false);
    } else {
      cancelAnimation(souffle);
      souffle.value = 0;
    }
  }, [actif, fini, reduit, souffle]);

  const trace = useAnimatedProps(() => ({ strokeDashoffset: CIRCONFERENCE * (1 - part.value) }));
  // Halo doux, immobile si les animations sont réduites ; éteint à la fin.
  const halo = useAnimatedStyle(() => ({
    opacity: fini ? 0 : 0.55 + 0.45 * souffle.value,
    transform: [{ scale: 0.92 + 0.08 * souffle.value }],
  }));

  return (
    <View style={styles.zone} accessibilityElementsHidden importantForAccessibility="no-hide-descendants" testID={fini ? 'anneau-fini' : 'anneau-progression'}>
      <Animated.View style={[styles.halo, halo]} pointerEvents="none">
        <Svg width={ZONE} height={ZONE}>
          <Defs>
            <RadialGradient id="haloAnneau" cx="50%" cy="50%" r="50%">
              <Stop offset="0.45" stopColor={colors.cyan} stopOpacity={0.28} />
              <Stop offset="1" stopColor={colors.cyan} stopOpacity={0} />
            </RadialGradient>
          </Defs>
          <Circle cx={ZONE / 2} cy={ZONE / 2} r={ZONE / 2} fill="url(#haloAnneau)" />
        </Svg>
      </Animated.View>
      <Svg width={TAILLE} height={TAILLE}>
        <Defs>
          <LinearGradient id="traitAnneau" x1="0" y1="0" x2="1" y2="1">
            <Stop offset="0" stopColor={gradients.accent[0]} />
            <Stop offset="1" stopColor={gradients.accent[1]} />
          </LinearGradient>
        </Defs>
        <Circle cx={TAILLE / 2} cy={TAILLE / 2} r={RAYON} stroke={colors.inkSoft} strokeWidth={TRAIT} fill="none" />
        <CercleAnime
          cx={TAILLE / 2}
          cy={TAILLE / 2}
          r={RAYON}
          stroke={fini ? colors.success : 'url(#traitAnneau)'}
          strokeWidth={TRAIT}
          strokeLinecap="round"
          strokeDasharray={CIRCONFERENCE}
          animatedProps={trace}
          fill="none"
          transform={`rotate(-90 ${TAILLE / 2} ${TAILLE / 2})`}
          testID="anneau-trace"
        />
      </Svg>
      <View style={styles.centre} pointerEvents="none">
        {fini ? (
          <Apparition effet="pop">
            <Check size={34} color={colors.success} strokeWidth={3} />
          </Apparition>
        ) : (
          <BrandLogo size={40} testID="anneau-logo" />
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  zone: { alignSelf: 'center', width: ZONE, height: TAILLE + 8, alignItems: 'center', justifyContent: 'center' },
  halo: { position: 'absolute', width: ZONE, height: ZONE, top: (TAILLE + 8 - ZONE) / 2 },
  centre: { ...StyleSheet.absoluteFillObject, alignItems: 'center', justifyContent: 'center' },
});
